// src/common/components/serviceModalContent/useServiceModal.ts
import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getApi } from "../../../api/api";
import { setClientUserId } from "../../../store/slices/executorSlice";
import { selectIsOnline } from "../../../store/slices/networkSlice";
import { delayAction } from "../../utils/functions/delayAction";
import { reloadUserData } from "../../utils/functions/reloadUserData";

type Stage = "input" | "loading" | "success" | "error";

export const useServiceModal = (onClose: () => void) => {
  const dispatch = useDispatch();
  const isOnline = useSelector(selectIsOnline);
  const [api] = useState(getApi);
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<Stage>("input");

  const handleCodeChange = useCallback((value: string) => {
    setCode(value);
  }, []);

  const disabled = code.length !== 6 || !isOnline;

  const handleVerify = useCallback(
    async (value: string) => {
      setStage("loading");
      await delayAction(700);
      try {
        const result = await api.useServiceOtp(value);
        if (result) {
          dispatch(setClientUserId(result.userId));
          await reloadUserData();
          setStage("success");
          await delayAction(1500);
          onClose();
        }
      } catch (err: any) {
        setStage("error");
        await delayAction(2000);
        setStage("input");
        setCode("");
      }
    },
    [api, dispatch, onClose],
  );

  const handleQrScan = useCallback(
    (value: string) => {
      handleVerify(value);
    },
    [handleVerify],
  );

  const handleSubmit = useCallback(() => {
    if (code.length !== 6 || stage !== "input" || !isOnline) return;
    handleVerify(code);
  }, [code, stage, isOnline, handleVerify]);

  return {
    code,
    stage,
    isOnline,
    disabled,
    setCode,
    handleCodeChange,
    handleQrScan,
    handleSubmit,
  };
};